import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import Treatments from "./components/treatments/Treatments";
import Treatdiagnose from "./components/treatdiagnose/Treatdiagnose";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { RootState } from "../../../redux/store";
import baseUrl from "../../../api/baseUrl";
import {
  setloadeActive,
  setloadeDeactive,
} from "../../../redux/loader/loaderActions";
import AddXray from "./components/addxray/AddXray";
import {
  addAditionalNote,
  addDiagnoseNote,
  addDiagnoses,
  addDiagnosesImage,
  addPrescriptions,
  addTreatmentImage,
} from "../../../redux/diagnosestate/diagnoseAction";
import { toast } from "react-toastify";
import socket, { getAuthTokenForSocket } from "../../../api/socket";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { withAppointmentIdQuery } from "../../../utils/appointmentLinkContext";

interface ImageData {
  id: string;
  url: string;
  file: any;
  base64: any;
}

const isPricedSubTreatment = (subItem: any) =>
  subItem?.selected_option === "treatment_paid" ||
  subItem?.selected_option === "treatment_stage";

/** Total bill only reflects treatments that have been completed. */
const isIncludedInPaymentSummary = (status: string) => status === "Complete";

const isSubTreatmentInPaymentSummary = (subItem: any) =>
  isPricedSubTreatment(subItem) && subItem?.status === "Complete";

/** Estimate only reflects treatments that are actively ongoing. */
const isIncludedInEstimate = (status: string) => status === "ongoing";

const isPlannedStatus = (status: string) => status === "planned";

/** Long term treatments track their own plan status separately (defaults to "planned"). */
const isLongTermPlanned = (item: any) =>
  item?.status === "long term" && isPlannedStatus(item?.planStatus || "planned");

/** Long term treatments only count toward the estimate while their plan status is "ongoing". */
const isLongTermEstimateReady = (item: any) =>
  item?.status === "long term" && (item?.planStatus || "planned") === "ongoing";

/** Long term treatments count toward the total bill once their plan status is "Complete". */
const isLongTermComplete = (item: any) =>
  item?.status === "long term" && (item?.planStatus || "planned") === "Complete";

const isSubTreatmentPlanned = (item: any, subItem: any) =>
  item?.status !== "long term" &&
  isPricedSubTreatment(subItem) &&
  isPlannedStatus(subItem?.status);

const isSubTreatmentInEstimate = (subItem: any) =>
  isPricedSubTreatment(subItem) && subItem?.status === "ongoing";

function TreatmentTable() {
  const diagnose = useSelector((state: RootState) => state.diagnose);
  const navigate = useNavigate();
  const { ID } = useParams();
  const [searchParams] = useSearchParams();
  const appointmentId = String(searchParams.get("appointmentId") ?? "").trim();
  const dispatch = useDispatch();
  const [paymentDetails, setpaymentDetails] = useState<any>({});
  const [allTeatment, setallTeatment] = useState([]);
  const [formData, setFormData] = useState<ImageData[]>([]);
  const [showDateWise, setShowDateWise] = useState(false);
  const savePendingTreatmentsRef = useRef<(() => Promise<boolean>) | null>(
    null
  );
  const hasPendingTreatmentsRef = useRef(false);

  const registerSavePending = useCallback(
    (saveFn: (() => Promise<boolean>) | null) => {
      savePendingTreatmentsRef.current = saveFn;
    },
    []
  );

  const registerHasPending = useCallback((pending: boolean) => {
    hasPendingTreatmentsRef.current = pending;
  }, []);

  const fetchData = useCallback(() => {
    if (!ID) return;

    dispatch(setloadeActive());
    baseUrl
      .get("treatment/find-treatment", {
        headers: { treatmentid: ID },
      })
      .then(async (response) => {
        setFormData([]);

        const {
          aditionalNote,
          patientComplain,
          diagnose,
          imageDiagnose,
          imageTreatment,
          prescription,
        } = response.data.data;

        function cleanDoseArray(data: any) {
          return data.map((item: any) => ({
            _id: item?._id || "",
            dose: item?.doseId || "",
            drugsId: item?.drugsId?._id || "",
            drugs: item?.drugsId?.drugs || "",
            type: item?.drugsId?.type || "",
            duration: item?.duration || "",
            frequency: item?.frequency || "",
            createdAt: item?.createdAt || "",
          }));
        }
        dispatch(addPrescriptions(cleanDoseArray(prescription) ?? []));

        dispatch(addAditionalNote(aditionalNote));
        dispatch(addDiagnoseNote(patientComplain));
        dispatch(addDiagnoses(diagnose));
        dispatch(addDiagnosesImage(imageDiagnose));
        dispatch(addTreatmentImage(imageTreatment));
        setallTeatment(response.data.data.treatment);

        let estimate = 0;
        let totals = 0;
        let planned = 0;

        response.data.data.treatment.forEach((item: any) => {
          if (item?.draft === true) return;
          if (isIncludedInPaymentSummary(item.status)) {
            totals += parseFloat(item.price);
          }
          // Long term: count the treatment's own price once when it's fully Complete; never sum its stages.
          if (isLongTermComplete(item)) {
            totals += parseFloat(item.price) || 0;
          }
          if (item?.status === "long term") {
            if (isLongTermEstimateReady(item)) {
              estimate += parseFloat(item.price) || 0;
            }
          } else if (isIncludedInEstimate(item.status)) {
            estimate += parseFloat(item.price) || 0;
          }
          if (item?.status !== "long term" && isPlannedStatus(item.status)) {
            planned += parseFloat(item.price) || 0;
          }
          // Long term: count the treatment's own price once when its plan is still "planned"; never sum its stages.
          if (isLongTermPlanned(item)) {
            planned += parseFloat(item.price) || 0;
          }
          item?.subTreatment?.forEach((subItem: any) => {
            if (isSubTreatmentInPaymentSummary(subItem)) {
              totals += parseFloat(subItem.price) || 0;
            }
            // Long-term parent price already represents the estimate; never add its stages
            if (item?.status !== "long term" && isSubTreatmentInEstimate(subItem)) {
              estimate += parseFloat(subItem.price) || 0;
            }
            if (isSubTreatmentPlanned(item, subItem)) {
              planned += parseFloat(subItem.price) || 0;
            }
          });
        });

        const paymentResponse = await baseUrl.get("payment/last-payment", {
          headers: { treatmentid: ID },
        });

        // setPayment(paymentResponse.data.data);

        let lastDue = 0;
        let lastTotal = 0;
        paymentResponse.data.data.forEach((item: any) => {
          lastDue +=
            parseFloat(item.total) -
            parseFloat(item.discount) -
            parseFloat(item.card) -
            parseFloat(item.bankTransfer) -
            parseFloat(item.cash);
          lastTotal += parseFloat(item.total);
        });

        setpaymentDetails((prevFormData: any) => ({
          ...prevFormData,
          total: totals,
          estimate: estimate,
          planned: planned,
          outStanding: totals - lastTotal + lastDue,
          paid: lastTotal - lastDue,
        }));
      })
      .catch(console.error)
      .finally(() => dispatch(setloadeDeactive()));
  }, [ID, dispatch]);

  useEffect(() => {
    fetchData();
  }, [ID, fetchData]);
  useEffect(() => {
    const calculatePayments = () => {
      let estimate = 0;
      let totals = 0;
      let planned = 0;

      allTeatment.forEach((item: any) => {
        if (item?.draft === true) return;
        if (isIncludedInPaymentSummary(item.status)) {
          totals += parseFloat(item.price) || 0;
        }
        // Long term: count the treatment's own price once when it's fully Complete; never sum its stages.
        if (isLongTermComplete(item)) {
          totals += parseFloat(item.price) || 0;
        }
        if (item?.status === "long term") {
          if (isLongTermEstimateReady(item)) {
            estimate += parseFloat(item.price) || 0;
          }
        } else if (isIncludedInEstimate(item.status)) {
          estimate += parseFloat(item.price) || 0;
        }
        if (item?.status !== "long term" && isPlannedStatus(item.status)) {
          planned += parseFloat(item.price) || 0;
        }
        // Long term: count the treatment's own price once when its plan is still "planned"; never sum its stages.
        if (isLongTermPlanned(item)) {
          planned += parseFloat(item.price) || 0;
        }

        item?.subTreatment?.forEach((subItem: any) => {
          if (isSubTreatmentInPaymentSummary(subItem)) {
            totals += parseFloat(subItem.price) || 0;
          }
          // Long-term parent price already represents the estimate; never add its stages
          if (item?.status !== "long term" && isSubTreatmentInEstimate(subItem)) {
            estimate += parseFloat(subItem.price) || 0;
          }
          if (isSubTreatmentPlanned(item, subItem)) {
            planned += parseFloat(subItem.price) || 0;
          }
        });
      });

      setpaymentDetails((prevFormData: any) => ({
        ...prevFormData,
        total: totals,
        estimate: estimate,
        planned: planned,
        outStanding: totals - (prevFormData.paid || 0),
      }));
    };

    calculatePayments();
  }, [allTeatment]);

  const handleSave = useCallback(async () => {
    dispatch(setloadeActive());
    try {
      if (savePendingTreatmentsRef.current) {
        const saved = await savePendingTreatmentsRef.current();
        if (!saved) return;
      }

      const token = getAuthTokenForSocket();
      socket.emit("addedTreatment", token);
      if (formData.length > 0) {
        const imagesData = formData.map((imageData) => imageData.base64);
        await baseUrl.post(
          "treatment/addxraytoTreatement",
          { image: imagesData },
          { headers: { treatmentid: ID } }
        );
      } else {
        toast("Saved");
      }
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(setloadeDeactive());
    }
  }, [dispatch, formData, ID]);

  const handleRemove = useCallback(
    (e: any) => {
      dispatch(setloadeActive());
      baseUrl
        .delete("treatment/remove-TreatmentImage", {
          headers: {
            treatmentid: ID,
            treatmentImageid: e,
          },
        })
        .then(() => fetchData())
        .catch(console.error)
        .finally(() => dispatch(setloadeDeactive()));
    },
    [ID, dispatch, fetchData]
  );

  const payment = useCallback(async () => {
    if (hasPendingTreatmentsRef.current) {
      alert("Please save or remove pending treatments before going to pay");
      return;
    }

    dispatch(setloadeActive());
    try {
      if (formData.length > 0) {
        const imagesData = formData.map((imageData) => imageData.base64);
        await baseUrl.post(
          "treatment/addxraytoTreatement",
          { image: imagesData },
          { headers: { treatmentid: ID } }
        );
      }
      navigate(
        withAppointmentIdQuery(`/client/payments/invoice/${ID}`, appointmentId)
      );
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(setloadeDeactive());
    }
  }, [dispatch, formData, ID, navigate, appointmentId]);

  const fetch = () => {
    fetchData();
  };

  const diagnoseImages = useMemo(() => diagnose.treatmentImage, [diagnose]);
  return (
    <>
      <div className="space-y-3">
        {/* Diagnosis Table Section */}
       
            <Treatdiagnose adult={true} />
         

        {/* Treatment Table Section */}
        <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">
                Treatment Table
              </h3>
              {showDateWise && (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Grouped by date and treatment category
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowDateWise((prev) => !prev)}
              className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold transition-all shrink-0 ${
                showDateWise
                  ? "bg-indigo-600 text-white shadow-md"
                  : "bg-indigo-500 text-white hover:bg-indigo-600 shadow-sm"
              }`}
              aria-pressed={showDateWise}
              title="Show treatments by date and category"
            >
              <CalendarMonthIcon sx={{ fontSize: 16 }} />
              {showDateWise ? "Hide Date-wise" : "Date-wise View"}
            </button>
          </div>
          <div className="p-3 overflow-x-auto">
            <Treatments
              allTeatment={allTeatment}
              setallTeatment={setallTeatment}
              fetchData={fetch}
              registerSavePending={registerSavePending}
              registerHasPending={registerHasPending}
              showDateWise={showDateWise}
            />
          </div>
        </div>

        {/* X-Ray Section */}
        <AddXray
          formData={formData}
          setFormData={setFormData}
          apiImage={diagnoseImages}
          handleRemove={handleRemove}
          title="Post treatment images"
        />

        {/* Payment Summary */}
        <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 p-4">
          <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-3">Payment Summary</h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {/* <div className="bg-violet-50 dark:bg-violet-900/20 rounded-lg p-3 text-center">
              <p className="text-[10px] text-violet-600 dark:text-violet-400 uppercase tracking-wide">Planned</p>
              <p className="text-base font-bold text-violet-700 dark:text-violet-300">Rs {paymentDetails?.planned || 0}</p>
            </div> */}
            <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3 text-center">
              <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide"> Estimated Treatment Plan Cost</p>
              <p className="text-base font-bold text-slate-800 dark:text-slate-200">Rs {paymentDetails?.estimate || 0}</p>
            </div>
            <div className="bg-indigo-50 dark:bg-primary/20 rounded-lg p-3 text-center">
              <p className="text-[10px] text-indigo-600 dark:text-slate-300 uppercase tracking-wide">Total Bill</p>
              <p className="text-base font-bold text-indigo-700 dark:text-slate-100">Rs {paymentDetails?.total || 0}</p>
            </div>
            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-3 text-center">
              <p className="text-[10px] text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">Paid</p>
              <p className="text-base font-bold text-emerald-700 dark:text-emerald-300">Rs {paymentDetails?.paid || 0}</p>
            </div>
            <div className={`rounded-lg p-3 text-center ${paymentDetails?.outStanding > 0 ? "bg-red-50 dark:bg-red-900/20" : "bg-emerald-50 dark:bg-emerald-900/20"}`}>
              <p className={`text-[10px] uppercase tracking-wide ${paymentDetails?.outStanding > 0 ? "text-red-600 dark:text-red-400" : "text-emerald-600 dark:text-emerald-400"}`}>Outstanding</p>
              <p className={`text-base font-bold ${paymentDetails?.outStanding > 0 ? "text-red-700 dark:text-red-300" : "text-emerald-700 dark:text-emerald-300"}`}>Rs {paymentDetails?.outStanding || 0}</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 justify-end">
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              className="px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 font-medium text-xs hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              Save
            </button>
            <button
              onClick={payment}
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-primary to-primaryLight text-white font-medium text-xs hover:shadow-lg hover:shadow-primary/20 transition-all"
            >
              Go to Pay
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default TreatmentTable;
